import { useEffect, useState } from 'react'

/**
 * Terminal colour theme picker. Swaps CSS variables on the .crt-screen
 * root via a data attribute / class so the palette (§g/§r/§c markup,
 * lineClass defaults, cursor) all reskin in one swap.
 *
 * Persists selection under `terminal-theme` in localStorage.
 */

export type TerminalTheme = 'green' | 'amber' | 'blue' | 'bloomberg' | 'paper'

const THEMES: { id: TerminalTheme; label: string; swatch: string }[] = [
  { id: 'green',     label: 'Phosphor',  swatch: '#00ff41' },
  { id: 'amber',     label: 'Amber',     swatch: '#ffb627' },
  { id: 'blue',      label: 'IBM Blue',  swatch: '#4fc3f7' },
  { id: 'bloomberg', label: 'Bloomberg', swatch: '#ff8000' },
  { id: 'paper',     label: 'Paper',     swatch: '#cfd8dc' },
]

const STORAGE_KEY = 'terminal-theme'

export function loadTheme(): TerminalTheme {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'green' || v === 'amber' || v === 'blue' || v === 'bloomberg' || v === 'paper') {
      return v
    }
  } catch {
    // localStorage blocked (private mode, sandbox) — fall through to default
  }
  return 'green'
}

export function useTerminalTheme(): [TerminalTheme, (t: TerminalTheme) => void] {
  const [theme, setTheme] = useState<TerminalTheme>(loadTheme)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      // non-fatal
    }
  }, [theme])

  return [theme, setTheme]
}

export function themeClass(theme: TerminalTheme): string {
  return theme === 'green' ? '' : `crt-theme-${theme}`
}

/** Inline dropdown — compact, sits next to the Ctrl+K / 2×2 buttons. */
export function ThemePicker({
  theme,
  onChange,
}: {
  theme: TerminalTheme
  onChange: (t: TerminalTheme) => void
}) {
  const [open, setOpen] = useState(false)
  const current = THEMES.find(t => t.id === theme) ?? THEMES[0]

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="text-crt-dim hover:text-crt-green font-mono text-xs transition-colors flex items-center gap-1.5"
        title="Terminal theme"
      >
        <span
          className="inline-block w-2.5 h-2.5 rounded-full border border-crt-dim/40"
          style={{ backgroundColor: current.swatch }}
          aria-hidden
        />
        <span>{current.label}</span>
      </button>
      {open && (
        <>
          {/* click-outside catcher */}
          <button
            type="button"
            aria-label="Close theme picker"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 cursor-default"
          />
          <div className="absolute right-0 top-full mt-1 z-50 min-w-[160px] bg-black/95 border border-crt-green/30 rounded font-mono text-xs shadow-lg shadow-black/80">
            {THEMES.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  onChange(t.id)
                  setOpen(false)
                }}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left transition-colors ${
                  t.id === theme
                    ? 'bg-crt-green/10 text-crt-bright'
                    : 'text-crt-dim hover:text-crt-green hover:bg-crt-green/5'
                }`}
              >
                <span
                  className="inline-block w-3 h-3 rounded-full border border-crt-dim/40 shrink-0"
                  style={{ backgroundColor: t.swatch }}
                  aria-hidden
                />
                <span className="flex-1">{t.label}</span>
                {t.id === theme && <span className="text-crt-green">✓</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
